/**
 * Analytics-ready tracking layer.
 *
 * Vendor-neutral wrapper that:
 *  1. Pushes structured events to `window.dataLayer` (always, even in dev).
 *  2. Forwards them to GA4 (`gtag`) once its script
 *     is loaded — which only happens on the PUBLISHED domain (see
 *     `initAnalytics`), so preview/localhost traffic is never counted.
 *
 * Correct the IDs below in this single block if they ever change.
 */

import { hasAnalyticsConsent } from "@/lib/consent";
import { forwardToMeta, metaPageView } from "@/lib/metaPixel";

// ─────────────────────────────────────────────────────────────
// Configurable IDs (update here only)
// Google measurement identifiers.
// ─────────────────────────────────────────────────────────────
/**
 * GA4 property. The connected Google Analytics account supplies the id at build
 * time; the literal is the fallback so tracking never silently stops if the
 * connector is unlinked.
 */
export const GA4_MEASUREMENT_ID =
  (import.meta.env['VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY'] as string | undefined)?.trim() ||
  "G-QJDQR23YPX";
export const GOOGLE_ADS_ID = "GT-5773CXXR";
export const GTM_CONTAINER_ID = "GTM-TL3HXTNR";

/** True when an ID looks like a real value rather than the shipped placeholder. */
function looksReal(id: string, placeholder: string): boolean {
  return !!id && id !== placeholder;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

let _initialized = false;

/**
 * Only fire real pixels on the published site. Preview hosts are
 * `*.lovable.app` ids/sandbox subdomains and localhost — those are excluded so
 * test traffic never pollutes analytics. The published custom/app domain is
 * everything else.
 */
function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return false;
  // Lovable preview sandboxes look like `id-preview--<uuid>.lovable.app`.
  if (host.includes("id-preview") || host.includes("--")) return false;
  return true;
}

function pushToDataLayer(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", payload);
  }
}

/**
 * Load Google measurement once on the published domain.
 */
export function initAnalytics() {
  if (_initialized || typeof window === "undefined") return;
  if (!isProductionHost()) {
    _initialized = true;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[analytics] pixels disabled on non-production host");
    }
    return;
  }
  // Measurement follows the saved consent choice.
  if (!hasAnalyticsConsent()) return;
  // Tag Manager (loaded from __root) is the only Google library; it forwards dataLayer events to GA4 + Ads.
  _initialized = true;

}

/** Fire a semantic event (sign_up, prediction_create, review_submit, ...). */
export function trackEvent(name: string, props: Record<string, unknown> = {}) {
  pushToDataLayer({ event: name, ...props, ts: Date.now() });
  forwardToMeta(name, props);
  if (isProductionHost() && typeof window.gtag === "function") {
    window.gtag("event", name, { ...props, send_to: GA4_MEASUREMENT_ID });
  }
}

/** Fire a page view. Called automatically on every route change. */
export function trackPageView(path: string, title?: string) {
  const page_title =
    title ?? (typeof document !== "undefined" ? document.title : undefined);
  pushToDataLayer({ event: "page_view", page_path: path, page_title, ts: Date.now() });
  metaPageView();
  if (isProductionHost() && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title,
      page_location: window.location.href,
      send_to: GA4_MEASUREMENT_ID,
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Conversion-event helpers (typed)
// Consent already gates initAnalytics() so these are safe to call anywhere.
// ─────────────────────────────────────────────────────────────

/** Fired when a user uploads a fixtures/odds file. */
export function trackUpload(params: { source?: string; rows?: number; fileType?: string }) {
  trackEvent("data_upload", {
    source: params.source ?? "unknown",
    rows: params.rows ?? 0,
    file_type: params.fileType ?? "unknown",
  });
}

/** Fired when a user (or admin) runs the prediction engine. */
export function trackRunPrediction(params: {
  engine?: "unified" | "safe" | "us-sports" | string;
  market?: string;
  matches?: number;
}) {
  trackEvent("run_predictions", {
    engine: params.engine ?? "unified",
    market: params.market ?? "all",
    matches: params.matches ?? 0,
  });
}

/** Fired when a user views the historical odds results page or tab. */
export function trackViewHistoricalOdds(params: {
  range?: string;
  filters?: Record<string, unknown>;
}) {
  trackEvent("view_historical_odds", {
    range: params.range ?? "all",
    filters: params.filters ?? {},
  });
}

/** Fired when a user reveals a prediction (spends credits). */
export function trackUnlock(params: { prediction_id: string; kind: "reveal" | "strategy"; cost: number }) {
  trackEvent("prediction_unlock", params);
}

/** Fired when a user shares a prediction. */
export function trackShare(params: { prediction_id?: string; channel: string; format?: string }) {
  trackEvent("prediction_share", {
    prediction_id: params.prediction_id ?? "",
    channel: params.channel,
    format: params.format ?? "card",
  });
}
